import React, { useMemo, useState } from "react";
import useSWR from "swr";
import { cn } from "@/lib/utils";
import { useUpdateLayerIcons } from "api";
import { boneParentMap } from "../data/boneTree";
import { Row } from "@tanstack/react-table";
import { Label } from "@/components/ui/label";
import Selector from "components/ui/Selector";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { LayerIcon } from "../types";

interface DataTableRowProps {
  row: Row<LayerIcon>;
  entityKey: string;
  type?: string;
}

export const DataTableRowSelect = ({
  row,
  entityKey,
  type,
}: DataTableRowProps) => {
  const data = row.original;

  const { data: LayerGroups = [] } = useSWR(["/layer-groups"]);

  const { handleUpdate, isMutating } = useUpdateLayerIcons(data.id);
  const [text, setText] = useState(data[entityKey] || "");
  const [isOpen, setIsOpen] = useState(false);
  const disabled = isMutating || data[entityKey] === text;

  const Options = useMemo(() => {
    if (entityKey === "gid") {
      return LayerGroups.map((item: any) => ({
        code: item.id,
        name: item.name,
      }));
    } else if (entityKey === "max") {
      return Array.from({ length: 4 }, (v, i) => ({
        code: i + 1,
        name: i + 1,
      }));
    } else if (entityKey === "bone") {
      return Object.keys(boneParentMap).map((bone) => ({
        code: bone,
        name: bone,
      }));
    }
    return [];
  }, [entityKey, LayerGroups]);

  const onSubmit = () => {
    if (disabled) {
      return null;
    }
    handleUpdate(entityKey, text, false);
    setIsOpen(false);
  };

  return (
    <div className="max-w-[500px] min-w-[130px] flex flex-col gap-2">
      <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <div
            onClick={() => setIsOpen(true)}
            className={cn(
              "flex h-8 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm transition-colors",
              "hover:outline-none hover:ring-1 hover:ring-ring hover:border-input hover:shadow-sm",
            )}
          >
            {Options?.find((item: any) => item.code === text)?.name ||
              text ||
              ""}
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {entityKey}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {entityKey}
              </Label>
              <Selector
                id={entityKey}
                className="col-span-3"
                datas={Options}
                update={(e: any) => setText(e)}
                value={text}
                labelName={entityKey}
                placeholder={`please select ${entityKey}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={isMutating || text === data[entityKey]}
              onClick={onSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
